import React from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@apollo/client'
import Load from './Load'
import {
    TORNEIOS_FILTRADOS,
    TorneiosFiltradosData,
    TorneiosFiltradosVars,
    Torneio,
} from '../services/torneioService'

import { getMonthAbbreviation } from '../utils/date'
import { Typography } from '@mui/material';
import { CATEGORIAS_TORNEIO_POR_ATLETA, CategoriaTorneioPorAtletaData, CategoriaTorneioPorAtletaVars } from '../services/categoriaTorneioService'


interface EventsProps {
    atletaId?: string
}

const Events: React.FC<EventsProps> = ({ atletaId }) => {
    // Quando não há atletaId, carrega os torneios vigentes (A-09: antes buscava a base
    // inteira sem status, sem data e sem paginação)
    const { data, loading, error } = useQuery<TorneiosFiltradosData, TorneiosFiltradosVars>(
        TORNEIOS_FILTRADOS,
        {
            variables: {
                apenasVigentes: true,
                page: 0,
                size: 5,
                sortBy: 'dataInicio',
                direction: 'ASC',
            },
            skip: !!atletaId,
        },
    )

    // Quando há atletaId, busca categorias do atleta (duas situações comuns)
    const {
        data: dataConfirmadas,
        loading: loadingConfirmadas,
        error: errorConfirmadas,
    } = useQuery<CategoriaTorneioPorAtletaData, CategoriaTorneioPorAtletaVars>(
        CATEGORIAS_TORNEIO_POR_ATLETA,
        {
            variables: { idAtleta: atletaId || '', statusInscricao: 'CONFIRMADA' },
            skip: !atletaId,
            fetchPolicy: 'no-cache',
        }
    )
    const {
        data: dataPendentes,
        loading: loadingPendentes,
        error: errorPendentes,
    } = useQuery<CategoriaTorneioPorAtletaData, CategoriaTorneioPorAtletaVars>(
        CATEGORIAS_TORNEIO_POR_ATLETA,
        {
            variables: { idAtleta: atletaId || '', statusInscricao: 'PENDENTE' },
            skip: !atletaId,
            fetchPolicy: 'no-cache',
        }
    )

    if ((!atletaId && loading) || (atletaId && (loadingConfirmadas || loadingPendentes))) {
        return <Load />
    }

    if (error || errorConfirmadas || errorPendentes) {
        return (
            <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                <div className="card-body text-center">
                    <p className="text-danger mb-0">Erro ao carregar torneios.</p>
                </div>
            </div>
        )
    }

    // Lista de torneios baseados na origem dos dados
    let torneios: Array<{
        id: string
        nome: string
        dataInicio: string
        local?: { nome?: string | null; endereco?: { cidade?: { nome?: string; uf?: string } } }
    }> = []

    if (atletaId) {
        const categorias = [
            ...(dataConfirmadas?.categoriaTorneioPorAtleta || []),
            ...(dataPendentes?.categoriaTorneioPorAtleta || []),
        ]
        // Deduplica por torneio.id
        const map = new Map<string, typeof torneios[number]>()
        categorias.forEach((c) => {
            const t = c.torneio
            if (t && t.dataInicio && !map.has(t.id)) {
                map.set(t.id, {
                    id: t.id,
                    nome: t.nome,
                    dataInicio: t.dataInicio || '',
                    local: t.local as any,
                })
            }
        })
        torneios = Array.from(map.values())
    } else {
        // dataInicio é opcional na API (criarTorneio aceita só nome); sem data,
        // o torneio não é exibível neste widget (ordenado por data) — mesmo
        // filtro já aplicado no ramo por atleta acima.
        torneios = (data?.torneiosFiltrados?.items || [])
            .filter((t) => !!t.dataInicio)
            .map((t) => ({
                id: t.id,
                nome: t.nome,
                dataInicio: t.dataInicio,
                local: t.local,
            }))
    }

    torneios = torneios.slice().sort(
        (a, b) => new Date(a.dataInicio).getTime() - new Date(b.dataInicio).getTime()
    )

    if (torneios.length === 0) {
        return (
            <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
                <div className="card-body d-flex align-items-center p-4 " >
                    <h4 className="fw-700 mb-0 font-xssss text-grey-900">Torneios</h4>
                </div>
                <div className="card-body pt-0 text-center">
                    <p className="mb-0">Nenhum torneio encontrado.</p>
                </div>
            </div>
        )
    }

    const eventsList = torneios.map((torneio) => {
        // Parse yyyy-mm-dd as local date
        const [year, month, day] = torneio.dataInicio.split('-').map(Number)
        const localDate = new Date(year, month - 1, day)
        return {
            date: localDate.getDate().toString(),
            month: getMonthAbbreviation(localDate),
            status:
                localDate.getTime() < Date.now()
                    ? 'bg-secondary'
                    : 'bg-primary',
            name: torneio.nome,
            location: torneio.local?.nome,
            cidade: torneio.local?.endereco?.cidade?.nome,
            uf: torneio.local?.endereco?.cidade?.uf
        }
    })

    return (
        <div className="card w-100 shadow-xss rounded-xxl border-0 mb-3">
            <div className="card-body d-flex align-items-center p-4">
                <h4 className="fw-700 mb-0 font-xssss text-grey-900">Torneios</h4>
                <Link
                    to={atletaId ? `/athlete/matches/${atletaId}` : '/defaultevent'}
                    className="fw-600 ms-auto font-xssss text-primary"
                >
                    Ver todos
                </Link>
            </div>
            {eventsList.map((value, index) => (
                <div
                    key={index}
                    className="card-body d-flex pt-0 ps-4 pe-4 pb-3 overflow-hidden" 
                    
                >
                    <div
                        className={` me-2 p-3 rounded-xxl ${value.status}`}
                        style={{ minWidth: '70px', height: '80px' }}
                    >
                        <h4 className="fw-700 font-lg ls-3 lh-1 text-white mb-0 text-center">
                            <span className="ls-1 d-block font-xsss text-white fw-600 text-uppercase">
                                {value.month}
                            </span>
                            <span className="d-block font-xl text-white fw-600">
                                {value.date}
                            </span>
                        </h4>
                    </div>
                    <h4 className="fw-700 text-grey-900 font-xssss mt-2">
                        <Typography variant="h6" className="fw-700 text-grey-900 font-xssss mt-2">
                            {value.name}{' '}
                        </Typography>
                        <span className="d-block font-xsssss fw-500 mt-1  text-grey-500">
                            {value.location}
                        </span>{' '}
                        <span className="d-block font-xsssss fw-500 mt-0  text-grey-500">
                            {value.cidade ? `${value.cidade}, ${value.uf}` : ' '}
                        </span>{' '}
                    </h4>
                </div>
            ))}
        </div>
    )
}

export default Events
